import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SetFunnelRequest, Space, UnnamedFunnel } from '@fullstory/proto/fs/services/dx-objects';
import { useNotifications } from '@fullstory/react-hooks';
import {
  getAllFunnels,
  getActiveSavedFunnelsById,
  getSavedFunnelsById,
  getFunnelsAccess,
  activeFunnelsList,
} from './selectors';
import { FunnelsRequestActions } from './actions';
import { AppState, AppDispatch } from '../../types';
import { fullstorySelectors } from '../../selectors';
import { hashObject } from '@fullstory/util/hash';
import { makeUseAccess, makeUseCreateAccess } from '../../utils/accessControl';
import { Computation } from '@fullstory/proto/fs/api/metrics/funnelmetrics';
import { generateRoutePath, FUNNEL_DETAILS_ROUTE } from '@fullstory/routes';
import { useRequestState } from '../../utils/hooks';
import { intl } from '@fullstory/intl';
import { FunnelsById } from './types';
import { filterOverlays } from '../overlays/utils';
import { SegmentRequestActions } from '../segments';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

const { getOrgId } = fullstorySelectors;

export const useFetchAllFunnels = (orgId: string) => {
  const dispatch = useDispatch<AppDispatch>();

  const fetchFunnels = useCallback(() => {
    dispatch(FunnelsRequestActions.getAllFunnels(orgId)).catch(() =>
      console.error('Failed to get funnels'),
    );
  }, [dispatch, orgId]);

  useEffect(() => fetchFunnels(), [fetchFunnels]);
  return fetchFunnels;
};

export const useAllSavedFunnels = (includeOverlayedObjects: boolean = false) => {
  const requestStateStatus = useRequestState((state: AppState) => state.funnels.allFunnels);

  const funnelsById = useSelector(getSavedFunnelsById);

  const filtered: FunnelsById = useMemo(
    () => (includeOverlayedObjects ? funnelsById : filterOverlays(funnelsById)),
    [includeOverlayedObjects, funnelsById],
  );

  return {
    ...requestStateStatus,
    byId: filtered,
    activeFunnels: useSelector(getAllFunnels),
  };
};

export const useActiveSavedFunnels = () => {
  const requestStateStatus = useRequestState((state: AppState) => state.funnels.allFunnels);

  return {
    ...requestStateStatus,
    byId: useSelector(getActiveSavedFunnelsById),
    activeFunnels: useSelector(activeFunnelsList),
  };
};

const emptyUnnamed = {
  isLoading: false,
  isReady: true,
  hasError: false,
  unnamedFunnel: undefined,
};
export const useFetchUnnamedFunnel = (hash: string) => {
  const orgId = useSelector(getOrgId);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, lastRequested, data } = useSelector(
    (state: AppState) => state.funnels.unnamedFunnels,
  );
  const unnamedFunnel = data?.[hash];
  useEffect(() => {
    if (hash && orgId && !unnamedFunnel && !loading && !error) {
      dispatch(FunnelsRequestActions.getUnnamedFunnel({ hash, orgId })).catch(() =>
        console.error('Failed to get temporary funnel state'),
      );
    }
  }, [dispatch, hash, unnamedFunnel, loading, error, orgId]);
  if (!hash) {
    return emptyUnnamed;
  }
  return {
    isLoading: loading,
    isReady: !loading && !error && lastRequested > -1,
    hasError: error,
    unnamedFunnel,
  };
};

/**
 * Returns callbacks to perform mutations on saved funnels
 */
export const useSavedFunnelsActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  const setFunnel = useCallback(
    async (
      request: Omit<SetFunnelRequest, 'orgId'>,
      successMessage: string,
      errorMessage: string,
      space?: Space,
    ) => {
      try {
        if (!request.funnel) throw new Error('invalid funnel');
        const response = await dispatch(
          FunnelsRequestActions.setFunnel({
            ...request,
            orgId,
            funnel: {
              ...request.funnel,
            },
          }),
        );
        if (!response.funnel?.funnel) throw new Error('invalid funnel');
        if (successMessage) {
          notifications?.success(successMessage);
        }
        if (request.visibility === Visibility.PUBLIC) {
          // Refresh in case any segments were updated
          dispatch(SegmentRequestActions.getAllSegments(orgId, true));
        }

        return response;
      } catch (e) {
        notifications?.error(errorMessage);
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  return useMemo(
    () => ({
      // We want this to happen transparently to the user, so no notifications are triggered.
      markViewed: (funnelId: string) =>
        dispatch(
          FunnelsRequestActions.markFunnelViewed({
            orgId,
            funnelId,
          }),
        ),
      create: async ({
        funnel,
        successMessage = intl.formatMessage({
          defaultMessage: 'Successfully created Funnel',
          id: 'st+SAv',
        }),
        errorMessage = intl.formatMessage({
          defaultMessage: 'Failed to create Funnel',
          id: 'K6M7fk',
        }),
      }: {
        funnel: Omit<SetFunnelRequest, 'orgId'>;
        successMessage?: string;
        errorMessage?: string;
      }) => {
        const request = { ...funnel };
        if (request.funnel) {
          request.funnel.id = '';
        }
        const response = await setFunnel(request, successMessage, errorMessage);
        return response;
      },
      duplicate: async (funnel: Omit<SetFunnelRequest, 'orgId'>) => {
        const payload = { ...funnel };
        if (payload.funnel) {
          payload.funnel.name = intl.formatMessage(
            {
              defaultMessage: '{funnelName} (copy)',
              id: 'v0c4jU',
            },
            {
              funnelName: payload.funnel.name,
            },
          );
          payload.funnel.id = '';
        }
        const response = await setFunnel(
          payload,
          intl.formatMessage({ defaultMessage: 'Successfully duplicated Funnel', id: 'HdXM86' }),
          intl.formatMessage({ defaultMessage: 'Failed to duplicate Funnel', id: 'tFLJs0' }),
        );
        return response;
      },
      update: async (payload: Omit<SetFunnelRequest, 'orgId'>) => {
        const response = await setFunnel(
          payload,
          intl.formatMessage({ defaultMessage: 'Successfully updated Funnel', id: 'vk91RM' }),
          intl.formatMessage({ defaultMessage: 'Failed to update Funnel', id: 'G+xtYz' }),
        );
        return response;
      },
      star: (funnelId: string) =>
        dispatch(FunnelsRequestActions.starFunnel({ orgId, funnelId, starred: true })).catch(() =>
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to star Funnel', id: 'gRjbm5' }),
          ),
        ),
      unstar: (funnelId: string) =>
        dispatch(FunnelsRequestActions.starFunnel({ orgId, funnelId, starred: false })).catch(() =>
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to unstar Funnel', id: 'Mw1F4c' }),
          ),
        ),
      archive: async (funnelId: string) => {
        try {
          const response = await dispatch(
            FunnelsRequestActions.archiveFunnel({
              orgId,
              funnelId,
              archived: true,
            }),
          );
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully archived Funnel.', id: 'Pu6O5Q' }),
          );
          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to archive Funnel.', id: 'CyqoID' }),
          );
          return null;
        }
      },
      restore: (funnelId: string) => {
        const unarchivedFunnelUrl = generateRoutePath(FUNNEL_DETAILS_ROUTE, {
          orgId,
          id: funnelId,
        });
        return dispatch(FunnelsRequestActions.archiveFunnel({ orgId, funnelId, archived: false }))
          .then(() =>
            notifications?.successLink(
              intl.formatMessage({
                defaultMessage: 'Successfully restored Funnel. ',
                id: 'SCZlu3',
              }),
              intl.formatMessage({ defaultMessage: 'See it now.', id: 'gzvnO9' }),
              unarchivedFunnelUrl,
            ),
          )
          .catch(() =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to restore Funnel.', id: '/UFX1f' }),
            ),
          );
      },

      createUnnamedFunnel: (orgId: string, funnel: UnnamedFunnel) => {
        const hash = `${hashObject(funnel)}`;
        dispatch(FunnelsRequestActions.createUnnamedFunnel({ hash, funnel, orgId })).catch(() =>
          console.error('Failed to save temporary funnel state'),
        );
        return hash;
      },
    }),
    [dispatch, orgId, setFunnel, notifications],
  );
};

export const useFunnelsAccess = makeUseAccess(FunnelsRequestActions.getAccess, getFunnelsAccess);

export const useFunnelsCreateAccess = makeUseCreateAccess(
  FunnelsRequestActions.getAccess,
  getFunnelsAccess,
);

export type FunnelComputationSettings = {
  computation: Computation;
  segmentId: string;
  isPast: boolean;
};
